import { Button } from "./ui/button";
import { Play, ArrowRight, Zap } from "lucide-react";
import { motion } from "motion/react";
import droneImage from '../assets/d86a56557fa8c793f24dfb903f154895a11f8c62.png';
import logokalpa from '../assets/logokalpa.png';
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Navbar } from "./Navbar";

export function Hero() {
  const { t } = useLanguage();

  // Smooth scroll to services section
  const handleExploreServices = () => {
    const section = document.getElementById('services');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    // The Hero Section with premium design, animations, and interactive elements
    <div className="relative min-h-screen overflow-hidden">
      {/* Premium Background with Drone Image */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${droneImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#051F20]/95 via-[#0B2B26]/90 to-[#163832]/80"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#051F20] via-transparent to-transparent"></div>
        </div>
        
        {/* Premium Circuit Pattern Overlay */}
        <motion.div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 25px 25px, #8EB69B 2%, transparent 0%), radial-gradient(circle at 75px 75px, #C9A961 2%, transparent 0%)`,
            backgroundSize: '100px 100px'
          }}
          animate={{
            backgroundPosition: ['0px 0px', '100px 100px']
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      {/* Premium Navigation with Logo */}
      <Navbar/>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-20 pt-32 pb-24">
        <div className="max-w-5xl">
          {/* Badge */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="inline-flex items-center gap-3 bg-[#051F20]/60 backdrop-blur-2xl px-8 py-4 rounded-full border border-[#C9A961]/30 mb-10 shadow-2xl"
          >
            <motion.div
              animate={{ 
                rotate: 360,
                scale: [1, 1.2, 1]
              }}
              transition={{ 
                rotate: { duration: 3, repeat: Infinity, ease: "linear" },
                scale: { duration: 2, repeat: Infinity }
              }}
            >
              <Zap className="h-5 w-5 text-[#C9A961] fill-[#C9A961]" />
            </motion.div>
            <p className="text-[#8EB69B] tracking-wider text-sm uppercase">Smart Agriculture Solutions</p>
          </motion.div>
          
          {/* Main Heading */}
          <motion.h1 
            className="text-7xl lg:text-9xl text-white mb-10 leading-[0.95] tracking-tight"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <motion.span
              className="block"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
            >
              {t('hero.title')}
            </motion.span>
            <motion.span 
              className="block text-transparent bg-clip-text bg-gradient-to-r from-[#8EB69B] via-[#C9A961] to-[#8EB69B] font-semibold drop-shadow-lg"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 }}
              style={{
                backgroundSize: '200% auto',
                WebkitTextStroke: '1px rgba(142, 182, 155, 0.3)',
              }}
            >
              {t('hero.titleHighlight')}
            </motion.span>
          </motion.h1>
          
          {/* Subtext */}
          <motion.p 
            className="text-[#DAF1DE]/90 text-2xl mb-14 max-w-3xl leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            {t('hero.subtitle')}
          </motion.p>
          
          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -8 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button onClick={handleExploreServices} className="bg-gradient-to-r from-[#235347] to-[#8EB69B] hover:from-[#163832] hover:to-[#235347] text-white px-12 py-8 text-xl shadow-2xl hover:shadow-[#8EB69B]/50 transition-all duration-300 rounded-2xl border-2 border-[#C9A961]/20 group relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-[#C9A961]/30 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '200%' }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 1
                  }}
                />
                <span className="relative z-10 flex items-center gap-3">
                  Explore Services
                  <motion.div
                    animate={{ x: [0, 8, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="h-6 w-6" />
                  </motion.div>
                </span>
              </Button>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05, y: -8 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button variant="outline" className="border-2 border-[#DAF1DE]/40 bg-[#051F20]/40 backdrop-blur-xl text-white hover:bg-[#DAF1DE]/10 px-12 py-8 text-xl transition-all duration-300 rounded-2xl group">
                <Play className="mr-3 h-6 w-6 fill-[#8EB69B] text-[#8EB69B] group-hover:scale-125 transition-transform" />
                Watch Demo
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Premium 3D Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-24">
          {[
            { number: "500+", label: "Farms Developed", icon: "🌾" },
            { number: "50K+", label: "Acres Managed", icon: "🚜" },
            { number: "98%", label: "Client Satisfaction", icon: "⭐" },
            { number: "24/7", label: "Support Available", icon: "💬" }
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 50, rotateX: -15, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
              transition={{ 
                delay: 1.5 + idx * 0.1,
                duration: 0.6,
                type: "spring",
                stiffness: 120
              }}
              whileHover={{ 
                y: -8,
                rotateX: 5,
                scale: 1.03,
                transition: { duration: 0.2 }
              }}
              style={{ 
                perspective: 1200,
                transformStyle: 'preserve-3d'
              }}
              className="group relative bg-gradient-to-br from-[#051F20]/60 to-[#0B2B26]/40 backdrop-blur-2xl p-6 rounded-2xl border-2 border-[#8EB69B]/20 hover:border-[#C9A961]/50 transition-all duration-500 shadow-xl"
            >
              {/* 3D Depth Layer */}
              <div 
                className="absolute inset-0 bg-gradient-to-br from-[#8EB69B]/10 via-transparent to-[#235347]/10 rounded-2xl"
                style={{ transform: 'translateZ(-10px)' }}
              />
              
              {/* Animated Glow */}
              <motion.div
                className="absolute -inset-1 bg-gradient-to-r from-[#C9A961]/0 via-[#C9A961]/30 to-[#C9A961]/0 rounded-2xl blur-xl opacity-0 group-hover:opacity-100"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity
                }}
              />
              
              {/* Shine Effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-[#DAF1DE]/10 to-transparent rounded-2xl"
                animate={{ x: ['-100%', '200%'] }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 4,
                  ease: "easeInOut"
                }}
              />
              
              <div className="relative z-10" style={{ transform: 'translateZ(15px)' }}>
                <motion.div 
                  className="text-3xl mb-2"
                  animate={{ 
                    y: [0, -5, 0],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  {stat.icon}
                </motion.div>
                
                <motion.div 
                  className="text-4xl lg:text-5xl text-white mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#8EB69B] group-hover:to-[#C9A961] transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                >
                  {stat.number}
                </motion.div>
                
                <p className="text-[#DAF1DE]/80 text-sm">{stat.label}</p>
              </div>
              
              {/* Corner Accent */}
              <div className="absolute top-2 right-2 w-10 h-10 border-t-2 border-r-2 border-[#C9A961]/30 group-hover:border-[#C9A961]/70 rounded-tr-2xl transition-colors duration-300"></div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Animated Decorative Elements */}
      <motion.div 
        className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#8EB69B]/15 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.15, 0.25, 0.15],
          x: [0, 50, 0],
          y: [0, -50, 0]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div 
        className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-[#C9A961]/15 rounded-full blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.25, 0.15, 0.25],
          x: [0, -50, 0],
          y: [0, 50, 0]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
}